import { conversationService } from '../services/conversation.service';
import {
  IConversationByIdPayload,
  IConversationInitiatePayload,
  IConversationListPayload,
} from '../types/conversation.service.type';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useConfig } from 'hooks/use-config';

const CONVERSATION_LIST_PAGE_SIZE = 10;

export const useGetConversationInitiation = (options: IConversationInitiatePayload) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['conversation-initiate', options],
    queryFn: () => conversationService.initiate(options, blocksKey),
    enabled: !!blocksKey,
  });
};

export const useGetConversationList = (payload: IConversationListPayload) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['conversation-list', payload],
    queryFn: () => conversationService.getConversationList(payload, blocksKey),
    enabled: !!payload.agent_id && !!payload.project_key && !!blocksKey,
  });
};

export const useGetConversationListInfinite = (
  payload: Omit<IConversationListPayload, 'offset' | 'limit'>
) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useInfiniteQuery({
    queryKey: ['conversation-list-infinite', payload],
    queryFn: ({ pageParam = 1 }) =>
      conversationService.getConversationList(
        {
          ...payload,
          limit: CONVERSATION_LIST_PAGE_SIZE,
          offset: pageParam,
        },
        blocksKey
      ),
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.sessions.length, 0);
      if (totalFetched < lastPage.total_count) {
        return allPages.length + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!payload.agent_id && !!payload.project_key && !!blocksKey,
  });
};

export const useGetConversationSessionById = (payload: IConversationByIdPayload) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['conversation', payload],
    queryFn: () => conversationService.getConversationSessionById(payload, blocksKey),
    enabled: !!payload.session_id && !!blocksKey,
  });
};
